<template>
  <section class="mb-20">
    <h3
      id="das-required"
      class="font-roboto text-xl font-bold mb-2">
      Data availability statement
    </h3>
    <p class="mb-4">
      We need a Data Availability Statement. Please see our website for information on how to prepare your
      <a
        id="lnk-dasCard-rscDas"
        target="_blank"
        class="underline text-royal-blue-100 hover:text-royal-blue-160 focus:text-royal-blue-160 visited:text-visited"
        href="https://www.rsc.org/journals-books-databases/author-and-reviewer-hub/authors-information/prepare-and-format/data-sharing/#dataavailabilitystatements">
        Data Availability Statement</a>.
    </p>
    <LoadingOverlay
      :isLoading="dasIsLoading"
      :customIconCssClasses="['w-8', 'h-8']"
      :customOverlayCssClasses="dasIsLoading ? [
        'absolute', 'z-50', 'inset-0',
        'flex', 'items-center', 'justify-center',
        'bg-white/60',
      ] : []">
      <FormRadio
        v-model="dasType"
        name="selectDasType"
        input-id="selectDasType"
        :value="true"
        labelText="Yes"
        :isDisabled="dasIsLoading"
      />
      <FormRadio
        v-model="dasType"
        name="selectDasType"
        input-id="rdoCustomDas"
        :value="false"
        labelText="No, this is a review-type manuscript and has no primary data."
        class="mb-5"
        :isDisabled="dasIsLoading"
      />
    </LoadingOverlay>
    <NotificationComponent
      v-if="hasReviewDasError"
      data-testid="failed-to-post-review-das"
      class="my-8"
      role="alert"
      variant="error"
      heading-text="Failed to add review-type manuscript Data Availability Statement"
      :heading-level="1">
      <template #body>
        <p>
          Failed to add your Data Availability Statement. Please retry or <a
            data-testid="email-customer-service-post-review-das"
            class="underline text-royal-blue-100 hover:text-royal-blue-160 focus:text-royal-blue-160 visited:text-visited"
            id="linkCustomerServicesReviewDas"
            href="mailto:manuscriptsupport@rsc.org?subject=RSC%20Submissions:%20Failed%20to%20add%20review-type%20manuscript%20Data%20Availability%20Statement">email
            customer services</a> if the issue persists.
        </p>
      </template>
    </NotificationComponent>
    <TabsComponent
      v-if="dasType && editDasFile"
      id="dastabs"
      @on-tab-click="handleTabclick"
      :defaultTab="activeTab"
      class="mt-4">
      <template #tabs>
        <span
          v-for="(item, index) in ['Write a statement', 'Upload a statement']"
          :key="index">
          {{ item }}</span>
      </template>
      <template #tab0>
        <div class="mt-4">
          <p class="mb-2">
            <b>The manuscript Data Availability Statement is:</b>
          </p>
          <div class="mb-4">
            <Field
              name="dasCustomContent"
              :rules="{'max-characters-length': [limits.DAS.characters.statement, 'Data Availability Statement must not exceed']}"
              v-slot="slotProps"
            >
              <div>
                <TipTapEditor
                  :modelValue="dasCustomContent"
                  @update:modelValue="value => handleTextChange({ html: value }, slotProps.field)"
                  name="dasCustomContent"
                  :dataTestId="['test-tipTap-das','das-text']"
                  :id="'dasCustomContent'"
                  :placeHolder="'Enter your Data Availability Statement...'"
                  :wordCountId="'dasCustomWordCounter'"
                  :enable-word-limit="false"
                  :word-counter="false"
                  counterType="characters"
                  :characterLimit="limits.DAS.characters.statement"
                />
              </div>
            </Field>
            <div
              v-show="dasCustomIsSent"
              id="pnl-dasCard-editIcon"
              class="mt-4">
              <ButtonComponent
                label="Edit"
                variant="secondary"
                @customClick="handleEditCustomDas">
                <template #icon>
                  <EditIcon />
                </template>
              </ButtonComponent>
            </div>
          </div>
          <ButtonComponent
            label="Add text"
            variant="secondary"
            :disabled="isDasCustomButtonDisabled"
            data-testid="upload-custom-das-text"
            id="dasCustomAddTextButton"
            @customClick="handleAction"
            v-show="!dasCustomIsSent"
          />
        </div>
        <NotificationComponent
          v-if="hasCustomDasError"
          data-testid="failed-to-post-custom-das"
          class="my-8"
          role="alert"
          variant="error"
          heading-text="Failed to add Data Availability Statement"
          :heading-level="1">
          <template #body>
            <p>
              Failed to add your Data Availability Statement. Please retry or <a
                data-testid="email-customer-service-post-custom-das"
                class="underline text-royal-blue-100 hover:text-royal-blue-160 focus:text-royal-blue-160 visited:text-visited"
                id="linkCustomerServicesCustomDas"
                href="mailto:manuscriptsupport@rsc.org?subject=RSC%20Submissions:%20Failed%20to%20add%20Data%20Availability%20Statement">email
                customer services</a> if the issue persists.
            </p>
          </template>
        </NotificationComponent>
      </template>
      <template #tab1>
        <div>
          <p class="mb-4">
            Word or PDF
          </p>
          <FormInputFile
            ref="uploadDasFile"
            id="uploadDasFile"
            :multiple="false"
            :accept="[
              'application/msword',
              'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
              '.doc',
              '.docx',
              'application/pdf',
              '.pdf',
              'application/x-latex',
              'text/plain',
              '.tex'
            ]"
            :disabled="isUploadButtonLoading"
            @custom-change="handleAction"
            fileGroupType="DAS"
            buttonDataTestId="upload-das-file"
            :inline="true"
            :showSelectedFiles="false"
          >
          </FormInputFile>
          <NotificationComponent
            v-if="hasUploadDasError"
            data-testid="failed-to-post-upload-das"
            class="my-8"
            role="alert"
            variant="error"
            heading-text="Upload error"
            :heading-level="1">
            <template #body>
              <UploadErrorMessage
                fileTypeLabel="Data Availability Statement"
                dataTestId="das"
                testId="Das"
              />
            </template>
          </NotificationComponent>
        </div>
      </template>
    </TabsComponent>
    <UploadedFileList :fileListType="SubmissionFileType.DAS">
      <template #editDAS="{ disabled }">
        <ButtonComponent
          label="Edit"
          variant="secondary"
          @customClick="handleEditCustomDas"
          :disabled="disabled"
          v-if="dasCustomIsSent">
          <template #icon>
            <EditIcon />
          </template>
        </ButtonComponent>
      </template>
    </UploadedFileList>
    <InlineWarningMessage
      class="mt-4"
      v-if="error ?? dasError"
      :errorMessage="error ?? dasError"
      errorTestId="das-upload"
    />
  </section>
</template>
<script setup lang="ts">
import TipTapEditor from '../common/TipTapEditor.vue'
import ButtonComponent from '../ui-components/ButtonComponent.vue'
import FormRadio from '../ui-components/FormRadio.vue'
import EditIcon from '../ui-components/icons/EditIcon.vue'
import FormInputFile from '../common/FormInputFile.vue'
import InlineWarningMessage from '../common/InlineWarningMessage.vue'
import TabsComponent from '../ui-components/TabsComponent.vue'
import NotificationComponent from '../ui-components/NotificationComponent.vue'
import UploadedFileList from '../upload/UploadedFileList.vue'
import UploadErrorMessage from './UploadErrorMessage.vue'
import { SubmissionFileType } from '@/types'
import { useDasUpload } from '../../composables/useDasUpload'
import { Field } from 'vee-validate'
import limits from '../../constants/limits'
import LoadingOverlay from '../ui-components/LoadingOverlay.vue'

const props = defineProps<{ error?: string }>()
const emit = defineEmits(['clear-error', 'update-error'])

const {
  hasCustomDasError,
  hasReviewDasError,
  hasUploadDasError,
  activeTab,
  isUploadButtonLoading,
  dasCustomIsSent,
  dasCustomContent,
  editDasFile,
  dasType,
  isDasCustomButtonDisabled,
  handleTabclick,
  handleEditCustomDas,
  handleAction,
  handleTextChange,
  dasError,
  dasIsLoading,
} = useDasUpload(emit, props)
</script>
